import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Globe2, UserPlus, CloudUpload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDateTime } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/admin/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { TabBar } from "@/components/admin/TabBar";

const TABS = [["", "All open"], ["new", "New"], ["in_progress", "In progress"], ["awaiting_records", "Awaiting records"], ["review", "In review"], ["on_hold", "On hold"], ["flagged", "Foreign review"], ["report_ready", "Report ready"], ["delivered", "Complete"], ["incomplete", "Incomplete"]];

export default function Cases() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const [q, setQ] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const { can } = useAuth();
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/cases", { params: { status: status || undefined, q: q || undefined } }).then((r) => setItems(status ? r.data : r.data.filter((c: any) => c.case_status !== "delivered"))), 200);
    return () => clearTimeout(t);
  }, [status, q]);
  return (
    <div>
      <PageHeader title="Case queue" subtitle="Submitted volunteer applications and their research progress."
        actions={can("manual_entry") && <Link to="/admin/cases/new" className="btn-primary btn-sm" data-testid="cases-manual-entry-button"><UserPlus className="h-4 w-4" /> Enter applicant manually</Link>} />
      <TabBar testId="cases-tabs" prefix="cases-tab" value={status} onChange={(v) => setParams(v ? { status: v } : {})} tabs={TABS.map(([v, l]) => [v, l])} />
      <div className="mb-5 mt-3 flex justify-center">
        <input className="field-input max-w-md" placeholder="Search name, code or organization…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cases-search-input" />
      </div>
      <Card>
        {items.length ? (
          <div className="overflow-x-auto">
            <table className="tbl" data-testid="cases-table">
              <thead><tr><th>Applicant</th><th>Organization</th><th>States</th><th>Searches</th><th>Status</th><th>{status === "incomplete" ? "Started" : "Submitted"}</th></tr></thead>
              <tbody>
                {items.map((c) => {
                  const name = [c.data?.personal?.first_name, c.data?.personal?.last_name].filter(Boolean).join(" ") || "(not entered yet)";
                  const pct = c.searches_total ? Math.round((c.searches_done / c.searches_total) * 100) : 0;
                  return (
                    <tr key={c.id} data-testid={`case-row-${c.id}`}>
                      <td>
                        <div className="flex items-center gap-1.5">
                          <Link to={`/admin/cases/${c.id}`} className="font-semibold text-[#1B365D] hover:underline" data-testid={`case-link-${c.id}`}>{name}</Link>
                          {c.airtable_synced_at && (
                            <span title={`Sent to Airtable (${c.airtable_table}) ${fmtDateTime(c.airtable_synced_at)}`} className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#E0F2FE] text-[#0369A1]" data-testid={`case-airtable-icon-${c.id}`}>
                              <CloudUpload className="h-3 w-3" aria-label="Sent to Airtable" />
                            </span>
                          )}
                        </div>
                        <p className="font-mono text-xs text-[#94A3B8]">{c.code || `Manual · ${c.received_via || "entry"}`}</p>
                      </td>
                      <td>{c.org_name}</td>
                      <td>
                        <div className="flex flex-wrap items-center gap-1">
                          {(c.states || []).map((s: string) => <span key={s} className="rounded bg-[#F1EFEA] px-1.5 py-0.5 font-mono text-xs font-semibold">{s}</span>)}
                          {c.flags?.foreign_address && <Globe2 className="h-4 w-4 text-[#D97706]" aria-label="Foreign address" />}
                        </div>
                      </td>
                      <td>
                        {c.status === "submitted" ? (
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-20 overflow-hidden rounded-full bg-[#F1EFEA]"><span className={`block h-full rounded-full ${pct === 100 ? "bg-[#15803D]" : "bg-[#1B365D]"}`} style={{ width: `${pct}%` }} /></span>
                            <span className="font-mono text-xs">{c.searches_done}/{c.searches_total}</span>
                          </div>
                        ) : <span className="text-xs text-[#94A3B8]">Step {(c.current_step || 0) + 1} of 4</span>}
                      </td>
                      <td><StatusBadge status={c.status === "submitted" ? c.case_status : c.status} /></td>
                      <td className="text-xs text-[#64748B]">{fmtDateTime(c.submitted_at || c.started_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <Empty text="No cases in this view." />}
      </Card>
    </div>
  );
}
